import {
  CheckBox,
  CtaButton,
  FancyRadioButton,
  Heading,
  Spinner,
  Subtitle,
  theme,
} from '@zappyrent/hundop';
import { keys, lowerCase, upperFirst } from 'lodash';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';

import { isInViewport } from '../../helpers/isInViewport';
import { useInput } from '../../hooks/useInput';
import { sendTicket } from '../../services/tickets';
import { FileInput } from '../UI/FileInput';
import { CATEGORIES, REQUESTER_TYPOLOGIES } from './constants';
import {
  StyledField,
  StyledFieldset,
  StyledForm,
  StyledRadioButtonsWrapper,
  StyledInput,
  StyledSelect,
  StyledTextArea,
  StyledError,
  StyledFormMessage,
  StyledConfirmation,
} from './HelpPageContent.styles';

const MAX_FILE_SIZE = 50000000;

export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = error => reject(error);
  });
};

const HelpPageContentForm = (): JSX.Element => {
  const { t } = useTranslation('help');

  const formMessageRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [formError, setFormError] = useState('');
  const [formConfirmation, setFormConfirmation] = useState('');

  const fullNameInput = useInput({ initialValue: '' });
  const emailInput = useInput({
    initialValue: '',
    validate: val => val.includes('@'),
  });
  const phoneNumberInput = useInput({ initialValue: '' });
  const requesterTypologyInput = useInput();
  const categoryInput = useInput({ initialValue: '' });
  const subcategoryInput = useInput({ initialValue: '' });
  const messageInput = useInput();
  const [file, setFile] = useState<File>();
  const [TOSAcceptance, setTOSAcceptance] = useState(false);
  const [token, setToken] = useState();

  const handleTOSAcceptanceChange = event => {
    setTOSAcceptance(event.target.checked);
  };

  const handleCaptchaChange = value => {
    setToken(value);
  };

  const isFileTooLarge = file && file.size > MAX_FILE_SIZE;

  const inputs = [
    fullNameInput,
    emailInput,
    phoneNumberInput,
    requesterTypologyInput,
    categoryInput,
    subcategoryInput,
    messageInput,
  ];

  useEffect(() => {
    subcategoryInput.setValue('');
    subcategoryInput.setDisabled(
      categoryInput.value === '' ||
        CATEGORIES[categoryInput.value ?? '']?.length === 0,
    );
  }, [categoryInput.value]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (
      inputs.filter(input => !input.disabled).some(input => !input.isValid) ||
      isFileTooLarge ||
      !token
    ) {
      setShowErrors(true);
      return;
    }

    setShowErrors(false);

    try {
      setIsSubmitting(true);
      await sendTicket({
        ticket: {
          requester: {
            name: fullNameInput.value ?? '',
            email: emailInput.value ?? '',
            phone: phoneNumberInput.value ?? '',
            typology: `v1_rt_${requesterTypologyInput.value}` ?? '',
          },
          category: `v1_cat_${categoryInput.value}` ?? '',
          subcategory: `v1_subcat_${subcategoryInput.value}` ?? '',
          message: messageInput.value ?? '',
        },
        attachment: file,
      });
      setFormConfirmation(t('requestSubmitted'));
      setFormError('');
      inputs.map(input => {
        input.setValue('');
      });
      setFile(undefined);
      setTOSAcceptance(false);
    } catch (err) {
      console.error(err);
      setFormError(t('errorWithRequest'));
    } finally {
      setIsSubmitting(false);
      if (formMessageRef.current && !isInViewport(formMessageRef.current))
        window.scrollBy(0, 160);
    }
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <Heading sm="24px" md="24px" lg="32px" tag="h2">
        {t('leaveUsAMessage')}
      </Heading>
      <StyledFieldset>
        <Subtitle tag="strong" as="legend">
          {t('personalInformation')}
        </Subtitle>
        <StyledField>
          <label htmlFor="full-name">{t('fullName')}</label>
          <StyledInput
            type="text"
            id="full-name"
            name="full-name"
            placeholder={t('insertYourFullName')}
            value={fullNameInput.value}
            onChange={fullNameInput.onChange}
          />
          {showErrors && fullNameInput.error && (
            <StyledError>{t(fullNameInput.error)}</StyledError>
          )}
        </StyledField>
        <StyledField>
          <label htmlFor="email">{t('email')}</label>
          <StyledInput
            type="email"
            id="email"
            name="email"
            placeholder={t('insertYourEmail')}
            value={emailInput.value}
            onChange={emailInput.onChange}
          />
          {showErrors && emailInput.error && (
            <StyledError>{t(emailInput.error)}</StyledError>
          )}
        </StyledField>
        <StyledField>
          <label htmlFor="phone-number">{t('phoneNumber')}</label>
          <StyledInput
            type="tel"
            id="phone-number"
            name="phone-number"
            placeholder={t('insertYourPhoneNumber')}
            value={phoneNumberInput.value}
            onChange={phoneNumberInput.onChange}
          />
          {showErrors && phoneNumberInput.error && (
            <StyledError>{t(phoneNumberInput.error)}</StyledError>
          )}
        </StyledField>
      </StyledFieldset>
      <StyledFieldset>
        <Subtitle tag="strong" as="legend">
          {t('requestDetails')}
        </Subtitle>
        <StyledField style={{ flex: '1 1 100%' }}>
          <label>{t('selectAnOption')}</label>
          <StyledRadioButtonsWrapper>
            {REQUESTER_TYPOLOGIES.map(reqTyp => (
              <FancyRadioButton
                key={reqTyp.value}
                id={`requester-radio-${lowerCase(reqTyp.value)}`}
                checked={requesterTypologyInput.value === reqTyp.value}
                name="requester-typology"
                label={upperFirst(t(reqTyp.label))}
                user="tenant"
                value={reqTyp.value}
                onChange={requesterTypologyInput.onChange}
              />
            ))}
          </StyledRadioButtonsWrapper>
          {showErrors && requesterTypologyInput.error && (
            <StyledError>{t(requesterTypologyInput.error)}</StyledError>
          )}
        </StyledField>
        <StyledField>
          <label htmlFor="category">{t('whatDoYouNeedHelpWith')}</label>
          <StyledSelect
            id="category"
            placeholder="Select category"
            value={categoryInput.value}
            onChange={categoryInput.onChange}
            isPlaceholderActive={!categoryInput.value}
          >
            <option disabled value="">
              {t('selectCategory')}
            </option>
            {keys(CATEGORIES).map(cat => (
              <option key={cat} value={cat}>
                {t(cat)}
              </option>
            ))}
          </StyledSelect>
          {showErrors && categoryInput.error && (
            <StyledError>{t(categoryInput.error)}</StyledError>
          )}
        </StyledField>
        <StyledField>
          <label htmlFor="subcategory">{t('canYouBeMoreSpecific')}</label>
          <StyledSelect
            id="subcategory"
            value={subcategoryInput.value}
            onChange={subcategoryInput.onChange}
            disabled={subcategoryInput.disabled}
            isPlaceholderActive={!subcategoryInput.value}
          >
            <option disabled value="">
              {t('selectSubcategory')}
            </option>
            {categoryInput.value &&
              CATEGORIES[categoryInput.value]?.map(subCat => (
                <option key={subCat} value={subCat}>
                  {t(subCat)}
                </option>
              ))}
          </StyledSelect>
          {!subcategoryInput.disabled &&
            showErrors &&
            subcategoryInput.error && (
              <StyledError>{t(subcategoryInput.error)}</StyledError>
            )}
        </StyledField>
        <StyledField style={{ flexGrow: 1 }}>
          <label htmlFor="message">{t('message')}</label>
          <StyledTextArea
            id="message"
            name="message"
            placeholder={t('insertMessage')}
            rows={4}
            {...messageInput}
          ></StyledTextArea>
          {showErrors && messageInput.error && (
            <StyledError>{t(messageInput.error)}</StyledError>
          )}
        </StyledField>
      </StyledFieldset>
      <StyledField>
        <label>{t('attachment')}</label>
        <FileInput file={file} onChange={setFile} />
        {showErrors && isFileTooLarge && (
          <StyledError>{t('theFileIsTooLarge')}</StyledError>
        )}
      </StyledField>
      <StyledField>
        <CheckBox
          id="tos"
          label={t('TOSAcceptance')}
          checked={TOSAcceptance}
          onChange={handleTOSAcceptanceChange}
        />
        {showErrors && !TOSAcceptance && (
          <StyledError>{t('thisFieldIsRequired')}</StyledError>
        )}
      </StyledField>
      <StyledField>
        <ReCAPTCHA
          sitekey={process.env.GATSBY_SITE_KEY as string}
          onChange={handleCaptchaChange}
        />
        {showErrors && !token && (
          <StyledError>{t('verifyThatYouAreNotARobot')}</StyledError>
        )}
      </StyledField>
      <CtaButton>{t('send')}</CtaButton>
      {(formConfirmation || formError || isSubmitting) && (
        <StyledFormMessage ref={formMessageRef}>
          {isSubmitting && (
            <Spinner
              foregroundColor={theme.colors.mainAlternate}
              size={14}
              strokeSize={2}
            />
          )}
          {!isSubmitting && (
            <>
              {formConfirmation && (
                <StyledConfirmation>{formConfirmation}</StyledConfirmation>
              )}
              {formError && <StyledError big>{formError}</StyledError>}
            </>
          )}
        </StyledFormMessage>
      )}
    </StyledForm>
  );
};

export default HelpPageContentForm;
